import { memo, type ReactNode } from "react";
import { SwitchIndicator } from "@design-system/Switch/SwitchIndicator";
import { PickerItem } from "./PickerItem";
import type { PickerItemProps } from "./Picker.types";
import styles from "./PickerItemPresets.module.scss";

type PresetItemProps = Omit<PickerItemProps, "children" | "leading">;

export interface PickerAccountItemProps extends PresetItemProps {
    name: string;
    mask: string;
    icon: ReactNode;
}

const PickerAccountItemImpl = ({
    name,
    mask,
    icon,
    ...itemProps
}: PickerAccountItemProps) => (
    <PickerItem
        {...itemProps}
        leading={<span className={styles.accountIcon}>{icon}</span>}
    >
        <span className={styles.accountRow}>
            <span className={styles.accountName}>{name}</span>
            <span className={styles.accountNumber}>
                <span className={styles.accountMask}>••</span>
                <span>{mask}</span>
            </span>
        </span>
    </PickerItem>
);

/**
 * Financial account row: institution icon, name and the masked last-four
 * cluster. Memoised, so the icon element it builds does not bust
 * `Picker.Item`'s memo while its own props hold.
 */
export const PickerAccountItem = memo(PickerAccountItemImpl);

export interface PickerToggleItemProps extends Omit<
    PresetItemProps,
    "onSelect" | "closeOnSelect" | "value"
> {
    children: ReactNode;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
}

const ToggleRow = ({
    children,
    checked,
    disabled,
}: {
    children: ReactNode;
    checked: boolean;
    disabled?: boolean;
}) => (
    <span className={styles.toggleRow}>
        <span>{children}</span>
        <SwitchIndicator checked={checked} disabled={disabled} />
    </span>
);

/**
 * Setting row: label then a `SwitchIndicator`, left-aligned. Selecting the row
 * flips the switch and keeps the popup open; the indicator is display-only so
 * the row is the single click target.
 */
export const PickerToggleItem = ({
    children,
    checked,
    onCheckedChange,
    disabled = false,
    description,
    destructive = false,
    className,
    ...itemProps
}: PickerToggleItemProps) => (
    <PickerItem
        {...itemProps}
        description={description}
        destructive={destructive}
        className={className}
        disabled={disabled}
        closeOnSelect={false}
        onSelect={() => onCheckedChange(!checked)}
    >
        <ToggleRow checked={checked} disabled={disabled}>
            {children}
        </ToggleRow>
    </PickerItem>
);
